/**
 * Confirmación de emisión = previsualización del lado del servidor (CP-588).
 *
 * Antes este diálogo calculaba los montos en el navegador a partir de la
 * grilla; el 2026-11-02 mostró 23,15M y la factura agrupada salió por 54,56M.
 * Ahora pide a `POST /billing/arca/preview` exactamente lo que va a salir a
 * AFIP por cada comprobante (los campos de FECAESolicitar, armados con las
 * mismas funciones que la emisión), las filas que lo componen y sus avisos, y
 * al confirmar manda las huellas: el backend se niega a emitir lo que haya
 * cambiado en el medio. Sin previsualización no hay botón de emitir.
 */
import { useEffect, useState } from "react";
import {
  Box, Stack, Typography, TextField, Button, Chip, Alert, CircularProgress, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  previewArca, fmtCbteFecha,
  type ArcaPreview, type ArcaPreviewInvoice, type CbteFechaStatus,
} from "../services/billing";
import { BRAND } from "../styles/theme";

function fmtMoney(v: number | null | undefined): string {
  return (v ?? 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtNro(ptovta: number, nro: number | null): string {
  return `${String(ptovta).padStart(5, "0")}-${nro ? String(nro).padStart(8, "0") : "????????"}`;
}

const STATUS_TEXT: Record<string, string> = {
  PENDING: "pendiente", PREPARED: "preparada", EMITTING: "emitiendo (reintento)", TIMEOUT: "timeout (reintento)",
  REJECTED: "rechazada antes", PARTIAL: "parcial",
};

/** Campos de FECAESolicitar, en el orden del XML, con una explicación corta. */
const AFIP_FIELDS: Array<[string, string]> = [
  ["CantReg", "cantidad de comprobantes en el pedido"],
  ["PtoVta", "punto de venta"],
  ["CbteTipo", "tipo de comprobante (1 = Factura A, 6 = Factura B)"],
  ["Concepto", "2 = servicios"],
  ["DocTipo", "80 = CUIT"],
  ["DocNro", "CUIT del receptor"],
  ["CbteDesde", "número del comprobante"],
  ["CbteHasta", "número del comprobante"],
  ["CbteFch", "fecha del comprobante"],
  ["ImpTotal", "importe total"],
  ["ImpTotConc", "no gravado"],
  ["ImpNeto", "neto gravado"],
  ["ImpOpEx", "exento"],
  ["ImpTrib", "otros tributos"],
  ["ImpIVA", "IVA"],
  ["FchServDesde", "servicio desde"],
  ["FchServHasta", "servicio hasta"],
  ["FchVtoPago", "vencimiento de pago"],
  ["MonId", "moneda"],
  ["MonCotiz", "cotización"],
  ["CondicionIVAReceptorId", "condición de IVA del receptor (RG 5616)"],
];

function AfipFields({ afip }: { afip: Record<string, unknown> }) {
  const iva = (afip.Iva as Array<Record<string, unknown>> | undefined)?.[0];
  return (
    <Table size="small" sx={{ "& td, & th": { py: 0.25, fontSize: 12 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Campo</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Qué es</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {AFIP_FIELDS.map(([k, help]) => (
          <TableRow key={k}>
            <TableCell sx={{ fontFamily: "monospace" }}>{k}</TableCell>
            <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>{afip[k] == null ? "— (no se manda)" : String(afip[k])}</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>{help}</TableCell>
          </TableRow>
        ))}
        {iva && (
          <TableRow>
            <TableCell sx={{ fontFamily: "monospace" }}>Iva / AlicIva</TableCell>
            <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>
              Id {String(iva.Id)} · BaseImp {String(iva.BaseImp)} · Importe {String(iva.Importe)}
            </TableCell>
            <TableCell sx={{ color: "text.secondary" }}>alícuota 5 = 21 %, base y monto de IVA</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function InvoiceCard({ inv, grouped }: { inv: ArcaPreviewInvoice; grouped: boolean }) {
  const [showAfip, setShowAfip] = useState(false);
  const border = !inv.ok ? "error.main" : inv.warnings.length ? "warning.main" : "success.main";
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderLeft: 4, borderLeftColor: border }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 800 }}>
          {inv.ok ? `${inv.cbte_tipo_desc} ${fmtNro(inv.ptovta, inv.cbtenro)}` : "No se emite"}
        </Typography>
        {inv.ok && inv.number_source !== "reservado" && (
          <Chip size="small" variant="outlined" label={inv.number_source === "estimado" ? "número estimado: lo asigna AFIP al emitir" : "número: lo asigna AFIP al emitir"} />
        )}
        {inv.ok && inv.number_source === "reservado" && <Chip size="small" variant="outlined" label="número ya reservado (reintento)" />}
        <Box sx={{ flexGrow: 1 }} />
        {inv.ok && (
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>${fmtMoney(inv.imp_total)}</Typography>
        )}
      </Stack>
      {inv.ok && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <b>{inv.receptor.razon_social || "(sin razón social)"}</b> · CUIT {inv.receptor.cuit} · {inv.receptor.condicion_iva_desc || `condición IVA ${inv.receptor.condicion_iva_id}`}
          {inv.receptor.email ? ` · ${inv.receptor.email}` : ""}
        </Typography>
      )}
      {inv.ok && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Emite {inv.emitter} (CUIT {inv.emitter_cuit}) · fecha {fmtCbteFecha(inv.fecha)} · servicio {fmtCbteFecha(inv.afip?.FchServDesde as string)} a {fmtCbteFecha(inv.afip?.FchServHasta as string)} ·
          neto ${fmtMoney(inv.imp_neto)} + IVA ${fmtMoney(inv.imp_iva)} = <b>${fmtMoney(inv.imp_total)}</b>
          {grouped ? ` · agrupada: ${inv.rows.length} fila${inv.rows.length === 1 ? "" : "s"} del CUIT` : ""}
        </Typography>
      )}
      {inv.errors.map((e, i) => <Alert key={i} severity="error" sx={{ mt: 1, py: 0 }}>{e}</Alert>)}
      {inv.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mt: 1, py: 0 }}>{w}</Alert>)}
      {inv.rows.length > 0 && (
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small" sx={{ "& td, & th": { py: 0.25 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Fila (colector)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Neto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>IVA</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Origen del monto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inv.rows.map((r) => (
                <TableRow key={r.collector} sx={{ bgcolor: r.edited ? "warning.light" : undefined }}>
                  <TableCell>{r.collector}{r.manual ? " (manual)" : ""}</TableCell>
                  <TableCell>{STATUS_TEXT[r.status ?? ""] ?? (r.status ?? "pendiente")}</TableCell>
                  <TableCell align="right">${fmtMoney(r.net_amount)}</TableCell>
                  <TableCell align="right">${fmtMoney(r.tax_amount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>${fmtMoney(r.amount)}</TableCell>
                  <TableCell>
                    {r.edited ? (
                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>
                        editado a mano{r.amount_before != null ? ` (antes $${fmtMoney(r.amount_before)})` : r.upstream_total != null ? ` (archivo: $${fmtMoney(r.upstream_total)})` : ""}
                        {r.amount_edited_by ? ` por ${r.amount_edited_by}` : ""}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {r.upstream_total != null ? "del archivo del período" : "sin total de referencia"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {inv.afip && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setShowAfip((v) => !v)} endIcon={showAfip ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
            Datos que se mandan a AFIP (FECAESolicitar)
          </Button>
          <Collapse in={showAfip}>
            <AfipFields afip={inv.afip} />
          </Collapse>
        </Box>
      )}
    </Paper>
  );
}

export default function ArcaPreviewDialog({
  open, onClose, ids, grouped, emitterCuit, cbteFecha, cbteFchManual, onCbteFchManual, busy, onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  /** `daterange|collector` seleccionados */
  ids: string[];
  grouped: boolean;
  emitterCuit: number | null;
  cbteFecha: CbteFechaStatus | null;
  cbteFchManual: string;
  onCbteFchManual: (v: string) => void;
  busy: boolean;
  /** se llama SOLO con los ids previsualizados OK y sus huellas */
  onConfirm: (ids: string[], expected: Record<string, string>) => void;
}) {
  const [preview, setPreview] = useState<ArcaPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const manual = cbteFecha?.editable && cbteFchManual ? cbteFchManual : undefined;

  useEffect(() => {
    if (!open || !ids.length) return;
    let alive = true;
    setPreview(null); setErr(null); setLoading(true);
    previewArca(ids, grouped, emitterCuit ?? undefined, manual)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ids.join(","), grouped, emitterCuit, manual]);

  const okInvoices = preview?.invoices.filter((i) => i.ok) ?? [];
  const failed = preview ? preview.invoices.length - okInvoices.length : 0;
  const withWarnings = okInvoices.filter((i) => i.warnings.length).length;
  const canConfirm = !!preview && !loading && !err && okInvoices.length > 0 && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Confirmar emisión: así van a salir las facturas a AFIP</DialogTitle>
      <DialogContent>
        {loading && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Armando los comprobantes con los mismos datos que se van a enviar…</Typography>
          </Stack>
        )}
        {err && (
          <Alert severity="error" sx={{ my: 1 }}>
            No se pudo previsualizar la emisión: {err}. Sin previsualización no se emite.
          </Alert>
        )}
        {preview && (
          <Box>
            <Alert severity={failed ? "warning" : "info"} sx={{ mb: 1.5 }}>
              Se van a emitir <b>{okInvoices.length}</b> comprobante{okInvoices.length === 1 ? "" : "s"} por{" "}
              <b>${fmtMoney(preview.total)}</b> (neto ${fmtMoney(preview.total_neto)} + IVA ${fmtMoney(preview.total_iva)}),
              emisor <b>{preview.emitter}</b>, {grouped ? "una factura por CUIT con todas sus filas pendientes" : "una factura por fila seleccionada"}.
              {failed > 0 && <> <b>{failed}</b> seleccionada{failed === 1 ? "" : "s"} no se {failed === 1 ? "emite" : "emiten"} (ver abajo).</>}
              {withWarnings > 0 && <> {withWarnings} con avisos para revisar.</>}
              {!preview.afip_verified && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>{preview.afip_note}</Typography>
              )}
            </Alert>
            {cbteFecha && (
              <Alert severity={cbteFecha.editable ? "warning" : "info"} sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
                  <Box>
                    Fecha del comprobante: <b>{fmtCbteFecha(manual ?? okInvoices[0]?.fecha ?? cbteFecha.fecha)}</b>
                    {cbteFecha.retroactiva && !manual && " (retroactiva al cierre del período)"}
                    {!cbteFecha.editable && cbteFecha.reason && (
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>No editable: {cbteFecha.reason}</Typography>
                    )}
                  </Box>
                  {cbteFecha.editable && (
                    <TextField
                      size="small" type="date" label="Fecha del comprobante"
                      value={cbteFchManual ? `${cbteFchManual.slice(0, 4)}-${cbteFchManual.slice(4, 6)}-${cbteFchManual.slice(6, 8)}` : ""}
                      onChange={(e) => onCbteFchManual(e.target.value.replace(/-/g, ""))}
                      inputProps={{
                        min: `${cbteFecha.period_end.slice(0, 4)}-${cbteFecha.period_end.slice(4, 6)}-${cbteFecha.period_end.slice(6, 8)}`,
                        max: `${cbteFecha.today.slice(0, 4)}-${cbteFecha.today.slice(4, 6)}-${cbteFecha.today.slice(6, 8)}`,
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                </Stack>
              </Alert>
            )}
            <Stack spacing={1.5}>
              {preview.invoices.map((inv) => <InvoiceCard key={inv.id} inv={inv} grouped={grouped} />)}
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" sx={{ bgcolor: BRAND.primaryLight, p: 1, borderRadius: 1 }} alignItems="center">
              <Typography sx={{ fontWeight: 700 }}>TOTAL a emitir ({okInvoices.length})</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 16 }}>${fmtMoney(preview.total)}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Al confirmar, el servidor vuelve a armar cada comprobante y lo compara con esta previsualización:
              si alguna fila cambió en el medio (otro usuario editó un monto, apareció o salió una fila del grupo),
              ese comprobante no se emite y hay que volver a abrir esta confirmación.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button
          variant="contained" color="primary" disabled={!canConfirm}
          onClick={() => preview && onConfirm(okInvoices.map((i) => i.id), preview.expected)}
        >
          Confirmar y emitir {okInvoices.length || ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
